/**
 * HTTP layer policy for the umi request plugin: bearer-token injection, the
 * translation of the API's `{ success, errorCode, errorMessage }` envelope into
 * thrown errors, and transparent recovery from an expired access token.
 *
 * Wired up by the `request` export in `src/app.tsx`.
 */
import type { RequestOptions } from '@@/plugin-request/request';
import { history, request } from '@umijs/max';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { refreshTokens } from './services/rest/api';
import { LOGIN_PATH } from '@/utils';

// Error handling plan: Error type
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// The response data format agreed with the backend
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * The in-flight refresh call, shared by every concurrent 401.
 *
 * The backend's refresh token is single-use - refreshAuth deletes it from the
 * Token collection the moment it is redeemed, then issues a new pair. A page
 * typically fires several requests together (influencer detail, bookmark,
 * feedback, ...), so when the access token expires several of them 401 at
 * once. Without this, each one called refreshTokens() independently: the
 * first redeemed the refresh token and rotated it, and every other call -
 * still holding the now-deleted token - got a *real* 401 back from the
 * refresh endpoint itself. That second failure had nothing to do with the
 * session actually being invalid, but it propagated up like any other 401 and
 * could reach `fetchUserInfo` in `src/app.tsx`, logging a still-valid user
 * out shortly after their access token's first expiry.
 *
 * Single-flighting fixes this at the root: only the first 401 starts a
 * refresh; every other one concurrent with it awaits that same promise
 * instead of redeeming the refresh token a second time.
 */
let refreshPromise: Promise<{ access: DATA.TokenDefine; refresh: DATA.TokenDefine }> | null = null;

const doRefresh = async () => {
  const res = await refreshTokens();
  if (!res.success) {
    throw new Error(res.errorMessage || 'Token refresh failed');
  }
  localStorage.setItem('accessToken', res.data.access.token);
  localStorage.setItem('refreshToken', res.data.refresh.token);
  return res.data;
};

/**
 * Give up on the session: clear the tokens that can no longer be trusted and
 * send the user to the login screen.
 *
 * Without this, an unrecoverable 401 just rejected its promise with nothing
 * downstream catching it - a click on "submit" would fail the request, the
 * session was in fact dead, and the user saw no error, no redirect, nothing.
 * Silence looked identical to success. This is the one place both
 * `handleTokenRefresh` failure paths (no refresh token to begin with, or the
 * refresh call itself rejected) end up, so every unrecoverable 401 now
 * produces the same visible outcome: back to the login screen.
 *
 * @param error - Rejected with, so the caller's own loading state still
 *   resolves (a spinner that never stops is its own silent-failure bug).
 */
const giveUpSession = (error: any) => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  history.push(LOGIN_PATH);
  return Promise.reject(error);
};

/**
 * Recover from an expired access token: swap it for a fresh pair, then replay
 * the request that just failed so the caller never sees the 401.
 *
 * Deliberately single-shot - it does not retry itself, so a refresh token that
 * is also expired falls through to `giveUpSession` rather than looping.
 */
const handleTokenRefresh = async (error: any) => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return giveUpSession(error);
  }

  try {
    // Join the in-flight refresh if there is one, rather than starting a
    // second one - see the comment on refreshPromise for why this matters.
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    const data = await refreshPromise;

    // Re-initiate the original request
    const originalRequest = error.config;
    originalRequest.headers.Authorization = `Bearer ${data.access.token}`;
    return request(originalRequest);
  } catch (err) {
    console.log(err);
    return giveUpSession(err);
  }
};

/**
 * Interceptors and error handling for the umi request plugin, consumed by the
 * `request` export in `src/app.tsx`.
 *
 * @doc https://umijs.org/docs/max/request
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    // The API answers 200 even for business failures, flagging them with
    // `success: false`. Turn those into real exceptions so a single catch site
    // covers both transport and business errors.
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }
    },
    /**
     * Single place where a failed request becomes user-visible feedback.
     *
     * Pass `skipErrorHandler: true` on a request to opt out and handle the
     * failure at the call site - login does this to render field-level errors.
     *
     * A 401 is checked before that opt-out, not after: token refresh is
     * self-repair, not error display, so a call site that owns its own error
     * UI (e.g. `doCreateQuestion` in threadContext.tsx, which just flips a
     * loading flag on failure) still gets a transparent retry instead of the
     * request dying silently the moment the access token expires.
     */
    errorHandler: (error: any, opts: any) => {
      if (error.response?.status === 401) {
        return handleTokenRefresh(error);
      }
      if (opts?.skipErrorHandler) throw error;
      // Business failure raised by `errorThrower` above: the backend picked how
      // it should be surfaced, so just honour `showType`.
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // The request reached the server, which answered outside the 2xx
        // range. 401 is already handled above, before skipErrorHandler; any
        // other status falls through silently on purpose.
      } else if (error.request) {
        // Request went out but no response came back (offline, timeout, CORS).
        // Left silent for now; uncomment to surface it.
        // message.error('None response! Please retry.');
      } else {
        // The request could not even be assembled.
        // message.error('Request error, please retry.');
      }
    },
  },

  // Attaches the bearer token to every outgoing call. Token state lives solely
  // in localStorage; there is no in-memory copy to keep in sync.
  requestInterceptors: [
    (config: RequestOptions) => {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }
      return { ...config };
    },
  ],

  // Pass-through today: the `success: false` case is already handled by
  // `errorThrower`, so there is nothing left to inspect here.
  responseInterceptors: [
    (response) => {
      return response;
    },
  ],
};
